<template>
  <article class="paper-card" :class="{'paper-card--read': isInHistory}" @click="handleOpen">
    <div class="paper-card__icon">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    </div>

    <div class="paper-card__body">
      <h3 class="paper-card__title">{{ item.title }}</h3>
      <p class="paper-card__meta">
        <span>{{ item.sourceTitle || item.sourceCategory }}</span>
        <span class="dot">·</span>
        <span>{{ timeAgo(item.pubDate) }}</span>
        <template v-if="firstAuthor">
          <span class="dot">·</span>
          <span>{{ firstAuthor }}</span>
        </template>
      </p>
      <p class="paper-card__description" v-if="item.description">{{ item.description }}</p>
    </div>

    <div class="paper-card__actions" @click.stop>
      <button
        @click="shareCard"
        class="paper-card__btn"
        :title="showCopied ? '¡Enlace copiado!' : 'Compartir'"
      >
        <svg v-if="!showCopied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="18" cy="5" r="3"/>
          <circle cx="6" cy="12" r="3"/>
          <circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </button>
      <button
        @click="toggleFavorite"
        class="paper-card__btn"
        :class="{'paper-card__btn--active': isFavorite}"
        :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </button>
      <button class="paper-card__pdf-btn" @click="handleOpen">
        {{ hasPdf ? 'Leer PDF' : 'Ver enlace' }}
      </button>
    </div>
  </article>
</template>

<script>
import { timeAgo } from '../../../utils/time.js';
import bookmarksService from '../../../services/bookmarks.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';

export default {
  name: 'PaperCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      updateTrigger: 0,
      showCopied: false
    };
  },
  computed: {
    isFavorite() {
      this.updateTrigger;
      return bookmarksService.isFavorite(this.item.id || this.item.link);
    },
    isInHistory() {
      this.updateTrigger;
      return bookmarksService.isInHistory(this.item.id || this.item.link);
    },
    hasPdf() {
      return !!this.item.linkPdf;
    },
    firstAuthor() {
      if (!this.item.author) return '';
      const authors = this.item.author.split(',').map(a => a.trim()).filter(Boolean);
      if (authors.length === 0) return '';
      return authors.length > 1 ? `${authors[0]} et al.` : authors[0];
    }
  },
  methods: {
    timeAgo,
    handleOpen() {
      bookmarksService.addToHistory(this.item);
      if (this.hasPdf) {
        this.$emit('open-pdf', this.item);
      } else {
        window.open(this.item.link, '_blank', 'noopener,noreferrer');
      }
    },
    async shareCard() {
      if (await copyShareUrl(this.item)) {
        this.showCopied = true;
        setTimeout(() => { this.showCopied = false; }, 2000);
      }
    },
    toggleFavorite() {
      if (this.isFavorite) {
        bookmarksService.removeFromFavorites(this.item.id || this.item.link);
      } else {
        bookmarksService.addToFavorites(this.item);
      }
    },
    handleBookmarkUpdate() {
      this.updateTrigger++;
    }
  },
  mounted() {
    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  }
};
</script>

<style scoped>
.paper-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 14px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.paper-card:hover {
  border-color: #4a9eff;
  transform: translateY(-1px);
}

.paper-card__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(74, 158, 255, 0.1);
  color: #4a9eff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.paper-card__body {
  flex: 1;
  min-width: 0;
}

.paper-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #e1e7ef;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.paper-card--read .paper-card__title {
  color: #a0aec0;
}

.paper-card__meta {
  font-size: 12px;
  color: #7d8694;
  margin: 0 0 6px 0;
  font-weight: 500;
}

.paper-card__meta .dot {
  margin: 0 6px;
}

.paper-card__description {
  font-size: 13px;
  line-height: 1.5;
  color: #9ca3af;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.paper-card__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.paper-card__btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #2d333b;
  background: #1c2127;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.paper-card__btn:hover {
  border-color: #4a5568;
  background: #262c34;
}

.paper-card__btn--active {
  background: white;
  border-color: white;
  color: #1c2127;
}

.paper-card__pdf-btn {
  padding: 8px 16px;
  background: rgba(74, 158, 255, 0.15);
  border: 1px solid rgba(74, 158, 255, 0.4);
  border-radius: 8px;
  color: #4a9eff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.paper-card__pdf-btn:hover {
  background: rgba(74, 158, 255, 0.25);
}

@media (max-width: 768px) {
  .paper-card {
    flex-wrap: wrap;
    padding: 14px 16px;
  }

  .paper-card__actions {
    width: calc(100% - 52px);
    justify-content: flex-end;
    margin-left: 52px;
  }
}
</style>
